import { useEffect } from "react"
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router"
import useDefaultLanding from "./useDefaultLanding"
import { useAuth, useRedirectAfterLogin } from "@/auth"
// Naming might be confusing, this wraps unauth'd routes
export default function AuthRoute() {
  const { isLoading, isAuthenticated } = useAuth()
  const { getRedirectUrl, peekRedirectUrl } = useRedirectAfterLogin()
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })

  // The magic-link route (/auth/link) is also wrapped by this layout, but
  // ApplyLoginLink does its own post-auth navigation there — including
  // honouring an invitation's explicit redirect to the questionnaire. Stay
  // out of its way: redirecting here too would consume the stored redirect
  // and race it to a different destination.
  // Pathname is locale-prefixed (e.g. /en/auth/link), so match the suffix.
  const ownsRedirect = !pathname.endsWith("/auth/link")

  // A redirect stored before login (a deep-link the User was bounced from)
  // wins over the computed default; only reach for the assignments-based
  // default landing when there is none.
  const hasStoredRedirect = isAuthenticated && !!peekRedirectUrl()
  const { route, isResolved } = useDefaultLanding(
    ownsRedirect && isAuthenticated && !isLoading && !hasStoredRedirect,
  )

  useEffect(() => {
    if (isLoading || !isAuthenticated || !ownsRedirect) {
      return
    }
    // Wait for the default to resolve, unless a stored redirect makes it moot.
    if (!hasStoredRedirect && !isResolved) {
      return
    }
    // getRedirectUrl returns (and consumes) the stored redirect when present,
    // falling back to the computed default otherwise.
    void navigate({ to: getRedirectUrl(route), replace: true })
  }, [
    isLoading,
    isAuthenticated,
    ownsRedirect,
    hasStoredRedirect,
    isResolved,
    route,
    navigate,
    getRedirectUrl,
  ])

  return (
    <div className="flex flex-col items-center px-8 py-24">
      <div className="w-full sm:w-2/3 md:w-1/2">
        <Outlet />
      </div>
    </div>
  )
}
